/* eslint-disable react/require-default-props */
import React from 'react';
import { withStateStore } from '@bingads-webui/with-state-store';
import type { StateStore } from '@bingads-webui/state-store';

import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import type { InstrumentationContextValue } from '@bingads-webui-react/hoc-with-instrumentation';
import { NotificationLogger, INotificationLogger } from '@bingads-webui-react/notification-telemetry';
import type { IInstrumentation } from '@bingads-webui-react/notification-telemetry';
import { TopBanner } from './top-banner';
import { notificationStore } from '../notification-store';
import {
  INotification,
  TopBannerMessageWrapperProps,
  TopBannerNotification,
} from '../types';

export interface TopBannerLayoutChange {
  topBanner: {
    mode: 'expanded' | 'collapsed';
  };
}

export interface ITopBannerLayoutProps {
  changeLayout: (
    layout: TopBannerLayoutChange,
    animate?: boolean,
    persist?: boolean,
    callback?: () => void
  ) => void;
  mode: string;
  bannerNotificationList: INotification[];
  Wrapper?: React.ComponentType<TopBannerMessageWrapperProps>;
  afterChangeLayout?: () => void;
  serverContext?: unknown;
}

class TopBannerLayoutInternal extends React.PureComponent<ITopBannerLayoutProps> {
  static contextType = InstrumentationContext;

  private telemetryLogger?: INotificationLogger;

  componentDidMount() {
    this.decideShowBanner();
  }

  componentDidUpdate() {
    this.decideShowBanner();
  }

  private getLogger(): INotificationLogger | undefined {
    const context = this.context as (InstrumentationContextValue & Partial<IInstrumentation>) | undefined;
    if (!context?.writeRawLog) return undefined;
    if (!this.telemetryLogger) {
      this.telemetryLogger = new NotificationLogger(context as IInstrumentation);
    }
    return this.telemetryLogger;
  }

  // eslint-disable-next-line react/sort-comp
  render() {
    const notifications: TopBannerNotification[] = this.props.bannerNotificationList.map(notification => ({
      notificationId: notification.id,
      message: notification.message,
      actionList: notification.actionList
    }));
    return (
      <TopBanner
        notifications={notifications}
        Wrapper={this.props.Wrapper}
        telemetryLogger={this.getLogger()}
        notificationDataList={this.props.bannerNotificationList}
        serverContext={this.props.serverContext}
      />
    );
  }

  showTopBanner() {
    this.props.changeLayout({ topBanner: { mode: 'expanded' } }, false, false, this.props.afterChangeLayout);
  }

  hideTopBanner() {
    this.props.changeLayout({ topBanner: { mode: 'collapsed' } });
  }

  decideShowBanner() {
    // If top banner is in hidden mode, we will never show it.
    const isHiddenMode = this.props.mode === 'hidden';
    if (isHiddenMode) {
      return;
    }

    if (this.props.bannerNotificationList.length !== 0) {
      this.showTopBanner();
    } else {
      this.hideTopBanner();
    }
  }
}

export const getTopBannerLayoutWithStore = (
  customizedStore: StateStore<Pick<ITopBannerLayoutProps, 'bannerNotificationList'>>,
  Wrapper?: React.ComponentType<TopBannerMessageWrapperProps>
) => withStateStore(TopBannerLayoutInternal, customizedStore, store => ({
  bannerNotificationList: store.state.bannerNotificationList,
  Wrapper,
}));

export const TopBannerLayout = getTopBannerLayoutWithStore(notificationStore);

export const TopBannerLayoutBase = TopBannerLayoutInternal;
